import React, { createContext } from 'react';
import propTypes from 'prop-types';

const dialogs = new WeakSet();
const activeDialogStack = [];
const getActiveDialog = () => activeDialogStack[activeDialogStack.length - 1];

export const DialogContext = createContext();

export function DialogProvider({ children }) {
  const focusFirstElement = () => {
    const { dialog, firstElement } = getActiveDialog();

    if (firstElement) {
      firstElement.focus();
    } else {
      dialog.focus();
    }
  };

  const focusLastElement = () => {
    const { dialog, lastElement } = getActiveDialog();

    if (lastElement) {
      lastElement.focus();
    } else {
      dialog.focus();
    }
  };

  const trapFocus = (e) => {
    const { dialog, lastElement } = getActiveDialog();

    if (dialog?.contains(e.relatedTarget)) {
      return;
    }

    e.stopPropagation();

    if (e.target === lastElement) {
      focusFirstElement();
    } else {
      focusLastElement();
    }
  };

  const handleEscape = (e) => {
    const { closeModal } = getActiveDialog();

    if (e.key === 'Escape') {
      e.stopPropagation();
      closeModal();
    }
  };

  const addListeners = ({ dialog }) => {
    dialog?.addEventListener('keydown', handleEscape);
    dialog?.addEventListener('focusout', trapFocus);
  };

  const lockScroll = () => {
    document.body.style.overflow = 'hidden';
  };

  const unlockScroll = () => {
    document.body.style.overflow = 'visible';
  };

  const removeListeners = ({ dialog }) => {
    dialog?.removeEventListener('focusout', trapFocus);
    dialog?.removeEventListener('keydown', handleEscape);
  };

  const deactivate = (refs) => {
    if (!dialogs.has(refs.dialog)) return;
    dialogs.delete(refs.dialog);

    removeListeners(refs);
    refs.modalTrigger?.focus();
    activeDialogStack.pop();

    if (getActiveDialog()) {
      addListeners(getActiveDialog());
    } else {
      unlockScroll();
    }
  };

  const activate = (refs) => {
    if (dialogs.has(refs.dialog)) return;
    dialogs.add(refs.dialog);

    if (getActiveDialog()) {
      removeListeners(getActiveDialog());
    }

    activeDialogStack.push(refs);
    addListeners(refs);
    focusFirstElement();
    lockScroll();
  };

  return (
    <DialogContext.Provider value={{ activate, deactivate }}>
      {children}
    </DialogContext.Provider>
  );
}

DialogProvider.propTypes = {
  children: propTypes.oneOfType([
    propTypes.arrayOf(propTypes.node),
    propTypes.node,
  ]).isRequired,
};
